import { useState, useCallback } from 'react';
import Lightbox from '@/components/base/Lightbox';

interface LightboxImage {
  src: string;
  alt: string;
  caption?: string;
}

const aboutImages: LightboxImage[] = [
  {
    src: "https://static.readdy.ai/image/86a2daa6c77fa935b9acdedfc02e0e91/e6abc8713798a7af259dc5f281991662.png",
    alt: "Instalaciones industriales de producción química en China",
    caption: "Planta de Producción Rixin Corp",
  },
  {
    src: "https://static.readdy.ai/image/86a2daa6c77fa935b9acdedfc02e0e91/30b4e42005f4fc1bb7e33124ead5147a.png",
    alt: "Equipos de proceso y reactores industriales",
    caption: "Reactores Industriales Rixin",
  },
  {
    src: "https://static.readdy.ai/image/86a2daa6c77fa935b9acdedfc02e0e91/d5d5efdab811151bcbeff623eacf9c9f.png",
    alt: "Almacén de tambores y logística de productos químicos",
    caption: "Almacén y Logística",
  },
  {
    src: "https://static.readdy.ai/image/86a2daa6c77fa935b9acdedfc02e0e91/a1dadae93c9adab81d7d4fc8a58f1d99.jpeg",
    alt: "Carga y transporte de IBCs para exportación",
    caption: "Exportación de IBCs",
  },
];

export default function AboutContent() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const openLightbox = useCallback((idx: number) => setLightboxIndex(idx), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const prevImage = useCallback(() => {
    setLightboxIndex((prev) => (prev !== null ? (prev === 0 ? aboutImages.length - 1 : prev - 1) : null));
  }, []);
  const nextImage = useCallback(() => {
    setLightboxIndex((prev) => (prev !== null ? (prev === aboutImages.length - 1 ? 0 : prev + 1) : null));
  }, []);

  return (
    <section id="quienes-somos" className="bg-background-50 py-16 md:py-24">
      <div className="w-full px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            {/* Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-6">
                <i className="ri-building-line text-sm w-4 h-4 flex items-center justify-center" />
                ¿Quiénes Somos?
              </div>

              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-6">
                Su Puente Comercial{' '}
                <span className="text-primary-500">China – Venezuela</span>
              </h2>

              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-5 text-justify">
                <strong className="text-foreground-950">RESINVENCA RESINAS DE VENEZUELA C.A.</strong> es una empresa venezolana 
                especializada en la importación y distribución de demulsificantes ya formulados, 
                provenientes de <strong className="text-foreground-950">Rixin Corp</strong>, uno de los 
                principales fabricantes de productos químicos para la industria petrolera en China.
              </p>

              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-5 text-justify">
                Nos encargamos de toda la cadena logística: desde la compra del producto terminado en China, 
                el transporte marítimo internacional, la gestión aduanal en Venezuela, hasta la entrega final 
                en los campos petroleros de la{' '}
                <strong className="text-foreground-950">Faja Petrolífera del Orinoco</strong>. 
                Usted recibe demulsificante de alta calidad, listo para usar, sin preocuparse por formulación 
                ni importación.
              </p>

              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-8 text-justify">
                Nuestro demulsificante importado ya ha sido probado y aprobado en los laboratorios de PDVSA, 
                posicionándose como una solución confiable para empresas que operan mediante contratos de 
                servicio con la industria petrolera nacional. Con el respaldo técnico de{' '}
                <strong className="text-foreground-950">Poly Alphas Chemicals C.A.</strong>, validamos 
                cada lote en condiciones reales de campo venezolano.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: 'ri-ship-line', text: 'Importación directa desde China' },
                  { icon: 'ri-drop-line', text: 'Producto terminado listo para usar' },
                  { icon: 'ri-oil-line', text: 'Especializados en la Faja del Orinoco' },
                  { icon: 'ri-flask-line', text: 'Fórmulas aprobadas por PDVSA' },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className={`${item.icon} text-sm`} />
                    </div>
                    <span className="text-foreground-700 text-sm font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Image Gallery */}
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              {aboutImages.map((img, idx) => (
                <div
                  key={img.src}
                  onClick={() => openLightbox(idx)}
                  className="group relative rounded-lg overflow-hidden border border-background-200/60 aspect-[4/3] cursor-pointer"
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                    <p className="text-white text-xs font-medium">{img.caption}</p>
                  </div>
                  <div className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-background-50/90 text-foreground-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <i className="ri-zoom-in-line text-xs" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      <Lightbox
        images={aboutImages}
        index={lightboxIndex}
        onClose={closeLightbox}
        onPrev={prevImage}
        onNext={nextImage}
      />
    </section>
  );
}